import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface NowShowingTitle {
  id: number;
  title: string;
  year: number | null;
  poster: string | null;
  rating: number;
  mediaType: "movie" | "tv";
}

const CACHE_KEY = "swiftix_now_showing_v1";
const TTL = 30 * 60 * 1000;

const readCache = (): NowShowingTitle[] | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; items: NowShowingTitle[] };
    if (!parsed?.items?.length || Date.now() - parsed.ts > TTL) return null;
    return parsed.items;
  } catch {
    return null;
  }
};

/**
 * Real trending titles from TMDB, usable without a session (the tmdb-search
 * function is public). The guest landing page uses this instead of hardcoded
 * poster lists so nothing on the front door is invented.
 */
export const useNowShowing = (limit = 12) => {
  const [titles, setTitles] = useState<NowShowingTitle[]>(() => readCache() ?? []);
  const [loading, setLoading] = useState(titles.length === 0);

  useEffect(() => {
    if (titles.length > 0) return;
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("tmdb-search", {
          body: { query: "", page: 1, filters: { mediaType: "all" }, trending: true },
        });
        if (error) throw error;
        const items: NowShowingTitle[] = (data?.results ?? [])
          .filter((r: { poster?: string | null; title?: string }) => r.poster && r.title)
          .slice(0, limit)
          .map((r: {
            id: number;
            title: string;
            year: number | null;
            poster: string | null;
            rating: number;
            media_type: "movie" | "tv";
          }) => ({
            id: r.id,
            title: r.title,
            year: r.year ?? null,
            poster: r.poster ?? null,
            rating: r.rating ?? 0,
            mediaType: r.media_type ?? "movie",
          }));
        if (cancelled || items.length === 0) return;
        setTitles(items);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items }));
        } catch {
          /* storage full — non-fatal */
        }
      } catch {
        /* the landing page degrades to copy-only, never an error state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [limit, titles.length]);

  return { titles, loading };
};

export default useNowShowing;
